import React, { useState, useEffect, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  Card,
  Form,
  Input,
  Button,
  Typography,
  message,
  Switch,
  Radio,
  Select,
  Upload,
  Modal,
  InputNumber,
  Checkbox,
} from "antd";
import {
  ArrowRightOutlined,
  CheckOutlined,
  CloseOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  FileImageOutlined,
  DownOutlined,
  FacebookOutlined,
  FileOutlined,
  FilePdfOutlined,
  GlobalOutlined,
  HomeOutlined,
  LinkedinOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  RedoOutlined,
  SearchOutlined,
  SendOutlined,
  SyncOutlined,
  UpOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import Spinner from "react-bootstrap/Spinner";
import CodeVerification from "../../Common/components/CodeVerification";
import AddressAutocomplete from "../AddressAutocomplete";
import * as com from "../../Common.js";
// import "./App.css";
// import "./Prequal.css";
import "./GMCCOnePager.css";
import {
  DECLARATION_QUESTIONS,
  LANGS,
  Lang,
  LangContext,
  MsgKey,
  Translate,
  initialLang,
  rememberLang,
  translate,
  useT,
} from "./onePagerI18n";

const { Title } = Typography;
const { Dragger } = Upload;

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  ssn?: string;
  ficoScore?: number;
  hasCoBorrower: boolean;
  // Co-borrower fields
  coFirstName?: string;
  coLastName?: string;
  coEmail?: string;
  coPhone?: string;
  coSsn?: string;
  coFicoScore?: number;
  loanPurpose: string;
  loanAmount?: number;
  downPayment?: number;
  isFirstTimeBuyer?: string;
  remainingBalance?: number;
  currentInterestRate?: number;
  hasSecondMortgage?: string;
  borrowAdditionalCash?: number;
  loanType: string;
  propertyType: string;
  purchasePrice: number;
  creditScore: string;
  closingDate?: any;
  notes?: string;
  // Property address fields
  propertyAddress?: string;
  // Purchase only: no property picked yet, so no address is asked or sent.
  stillShopping?: boolean;
  propertyUnit?: string;
  propertyState?: string;
  propertyCounty?: string;
  propertyCity?: string;
  propertyZip?: string;
  propertyValue?: number;
  propertyUse?: string;
  declaration_A?: string;
  declaration_B?: string;
  declaration_C?: string;
  declaration_D?: string;
  declaration_E?: string;
  declaration_F?: string;
  declaration_G?: string;
  declaration_H?: string;
  declaration_I?: string;
  declaration_J?: string;
  declaration_K?: string;
  declaration_L?: string;
  declaration_M?: string;
  declaration_D1?: string;
  declaration_D2?: string;
  declaration_A1?: string;
  declaration_C1?: string;
  declaration_A1_Type?: string;
  declaration_A1_Status?: string;
  co_declaration_A?: string;
  co_declaration_B?: string;
  co_declaration_C?: string;
  co_declaration_D1?: string;
  co_declaration_D2?: string;
  co_declaration_E?: string;
  co_declaration_F?: string;
  co_declaration_G?: string;
  co_declaration_H?: string;
  co_declaration_I?: string;
  co_declaration_J?: string;
  co_declaration_K?: string;
  co_declaration_L?: string;
  co_declaration_M?: string;
  co_declaration_A1?: string;
  co_declaration_C1?: string;
  co_declaration_A1_Type?: string;
  co_declaration_A1_Status?: string;
}

// ==================== 2026-09 redesign ====================
// "GMCC One-Page Application Redesign": one page in five sections on a
// desktop, with a progress panel and the loan officer beside it; one section
// per step on a phone. Field names, values and rules are the ones the
// application has always posted -- only purchasePrice is new.

const SECTIONS: { id: string; title: MsgKey; optional?: boolean }[] = [
  { id: "gop-about", title: "secAbout" },
  { id: "gop-loan", title: "secLoan" },
  { id: "gop-property", title: "secProperty" },
  { id: "gop-declarations", title: "secDeclarations" },
  { id: "gop-documents", title: "secDocuments", optional: true },
];

// The footer a customer's own website carries, repeated under the form and
// the thank-you screen (GMCC: gmccloan.com's). Keyed by customer id; a
// customer without an entry gets no footer. The legal lines are the
// customer's own words and stay in English; the generic labels translate.
type CustomerFooter = {
  company: string;
  nmls: string;
  links: { label: MsgKey; href: string }[];
  badges: string[];
  consumerAccess: string;
  licensing: string[];
  complaintsEmail: string;
  address: string;
  phone: string;
  email: string;
  social: { kind: "facebook" | "linkedin"; href: string }[];
  equalHousingLogo?: string;
};

const CUSTOMER_FOOTERS: Record<string, CustomerFooter> = {
  gmcc: {
    company: "General Mortgage Capital Corporation",
    nmls: "254895",
    links: [
      { label: "privacyPolicy", href: "https://www.gmccloan.com/PrivacyPolicy.html" },
      { label: "disclosures", href: "https://www.gmccloan.com/Disclosures.html" },
      { label: "termsAndConditions", href: "https://www.gmccloan.com/TermsandConditions.html" },
    ],
    badges: ["Fair Lender", "Fair Housing"],
    consumerAccess: "https://www.nmlsconsumeraccess.org/",
    licensing: ["Licensed Mortgage Banker-NYS Department of Financial Services"],
    complaintsEmail: "compliance@gmccloan.com",
    address: "1350 Bayshore Hwy, Suite 740, Burlingame CA 94010",
    phone: "650-340-7800",
    email: "info@gmccloan.com",
    social: [
      { kind: "facebook", href: "https://www.facebook.com/Generalmortgage" },
      { kind: "linkedin", href: "https://www.linkedin.com/company/general-mortgage-capital-corporation/" },
    ],
    equalHousingLogo: "/images/equal_housing_oppotunity.png",
  },
};

// A customer whose applications stop in the LOS (no Encompass) does not put
// the 1003 declarations on the one-pager: the loan officer takes them in the
// interview. The section goes, and with it its step and its number.
const sectionsFor = (withDeclarations: boolean) =>
  withDeclarations ? SECTIONS : SECTIONS.filter((s) => s.id !== "gop-declarations");

const DECLARATION_KEYS = ["A", "B", "C", "D1", "D2", "E", "F", "G", "H", "I", "J", "K", "L", "M"];

// The 1003 declaration questions, in each language, are in onePagerI18n.ts.

// A borrower picks a range -- few know their score, and "a best guess is
// fine". Each range posts its midpoint, rounded up; the two open ends are
// closed by the FICO scale itself (300-850), so "760+" posts 805 and
// "Below 640" 470. "Not sure" posts nothing.
export const ficoRanges = (t: Translate) => [
  { value: 805, label: "760+" },
  { value: 740, label: "720–759" },
  { value: 700, label: "680–719" },
  { value: 660, label: "640–679" },
  { value: 470, label: t("below640") },
  { value: 0, label: t("notSure") },
];

const PURPOSE_OPTIONS: { value: string; title: MsgKey; sub: MsgKey; icon: React.ReactNode }[] = [
  { value: "purchase", title: "buyHome", sub: "buyHomeSub", icon: <HomeOutlined rev={undefined} /> },
  { value: "refinance", title: "refinance", sub: "refinanceSub", icon: <SyncOutlined rev={undefined} /> },
];

// No "FHA secondary residence" here: it is a HUD exception a borrower cannot
// pick for themselves, and picked by mistake it became "Second Home" in
// Encompass with the FHA flag lost. The loan officer sets it when it applies.
// (Declaration A1's list of properties owned keeps it: that is the 1003's own.)
const propertyUseOptions = (t: Translate) => [
  { value: "primary", label: t("usePrimary") },
  { value: "secondary", label: t("useSecond") },
  { value: "investment", label: t("useInvestment") },
];

export const EXAMPLE_DOCUMENTS: MsgKey[] = [
  "exPaystubs",
  "exW2",
  "exBank",
  "exLicense",
  "exTaxes",
  "exInsurance",
  "exPurchase",
];

export const yesNo = (t: Translate) => [
  { label: t("yes"), value: "yes" },
  { label: t("no"), value: "no" },
];

export const moneyInputProps = {
  prefix: "$",
  controls: false,
  min: 0,
  formatter: (value: any) => (value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""),
  parser: (value: any) => (value ? `${value}`.replace(/\$|,/g, "") : ""),
};

const filled = (v: any) => v !== undefined && v !== null && `${v}`.trim() !== "";

// A buyer gives the price and the down payment; the loan is the difference.
// The server derives the same when this posts 0.
const loanAmountFor = (v: any): number => {
  if (v.loanPurpose !== "purchase") return Number(v.loanAmount || "");
  const price = Number(v.purchasePrice || 0);
  const down = Number(v.downPayment || 0);
  return price > down ? price - down : 0;
};

// Which step a field is on, for the phone layout's per-step validation.
const LOAN_FIELDS = ["loanPurpose", "purchasePrice", "downPayment", "isFirstTimeBuyer", "remainingBalance", "currentInterestRate", "hasSecondMortgage", "borrowAdditionalCash", "loanAmount"];
const stepOfField = (name: string): number => {
  if (/declaration_/.test(name)) return 3;
  if (/^property/.test(name)) return 2;
  if (LOAN_FIELDS.includes(name)) return 1;
  return 0;
};

// The first four sections' completeness as "1010" -- a string, so the
// watchers below re-render only when a section flips. (useWatch keeps the
// selector it was first given, so it must not close over props.)
const sectionState = (v: any): string => {
  if (!v) return "0000";
  const co = !!v.hasCoBorrower;
  const about =
    ["firstName", "lastName", "email", "phone"].every((k) => filled(v[k])) &&
    (!co || ["coFirstName", "coLastName", "coEmail", "coPhone"].every((k) => filled(v[k])));
  const loan = v.loanPurpose === "refinance" ? filled(v.remainingBalance) : filled(v.purchasePrice);
  const located =
    (v.loanPurpose !== "refinance" && !!v.stillShopping) ||
    (filled(v.propertyState) && (filled(v.propertyCity) || filled(v.propertyAddress)));
  const property = located && filled(v.propertyUse);
  const declarations =
    DECLARATION_KEYS.every((k) => filled(v[`declaration_${k}`])) &&
    (!co || DECLARATION_KEYS.every((k) => filled(v[`co_declaration_${k}`])));
  return [about, loan, property, declarations].map((d) => (d ? "1" : "0")).join("");
};

export const formatBytes = (n?: number) => {
  if (!n) return "";
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const MOBILE_QUERY = "(max-width: 767px)";
const useIsMobile = () => {
  const [mobile, setMobile] = useState<boolean>(
    () => typeof window !== "undefined" && window.matchMedia(MOBILE_QUERY).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setMobile(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return mobile;
};

interface LoanOfficerInfo {
  first_name: string;
  last_name: string;
  nmls: string;
  phone: string;
  email: string;
  // false for customers whose applications stop in the LOS (no Encompass,
  // no Consumer Connect email); the thank-you screen reads it.
  encompass_enabled?: boolean;
}

// Purchase / refinance as two cards; a Form.Item control (value/onChange).
export const ChoiceCards: React.FC<{
  value?: string;
  onChange?: (v: string) => void;
  onPick?: (v: string) => void;
}> = ({ value, onChange, onPick }) => {
  const t = useT();
  return (
  <div className="gop-choices" role="radiogroup">
    {PURPOSE_OPTIONS.map((o) => (
      <button
        type="button"
        key={o.value}
        role="radio"
        aria-checked={value === o.value}
        className={`gop-choice${value === o.value ? " is-on" : ""}`}
        onClick={() => {
          onChange?.(o.value);
          onPick?.(o.value);
        }}
      >
        <span className="gop-choice-icon">{o.icon}</span>
        <span className="gop-choice-text">
          <span className="gop-choice-title">{t(o.title)}</span>
          <span className="gop-choice-sub">{t(o.sub)}</span>
        </span>
      </button>
    ))}
  </div>
  );
};

// The desktop side panel: how far along the borrower is, and a way to jump.
const ProgressPanel: React.FC<{ form: any; fileCount: number; sections: typeof SECTIONS }> = ({
  form,
  fileCount,
  sections,
}) => {
  const t = useT();
  const watched = Form.useWatch((v: any) => sectionState(v), form) || "0000";
  const doneById: Record<string, boolean> = {
    "gop-about": watched[0] === "1",
    "gop-loan": watched[1] === "1",
    "gop-property": watched[2] === "1",
    "gop-declarations": watched[3] === "1",
    "gop-documents": fileCount > 0,
  };
  const done = sections.map((s) => !!doneById[s.id]);
  const doneCount = done.filter(Boolean).length;
  const [active, setActive] = useState<number>(0);
  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        let current = 0;
        sections.forEach((s, i) => {
          const el = document.getElementById(s.id);
          if (el && el.getBoundingClientRect().top <= 160) current = i;
        });
        setActive(current);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
    };
  }, [sections]);
  return (
    <div className="gop-panel">
      <div className="gop-eyebrow">{t("yourApplication")}</div>
      <div className="gop-meter">
        <div className="gop-meter-fill" style={{ transform: `scaleX(${doneCount / sections.length})` }} />
      </div>
      <div className="gop-meter-text">
        {t("progress", { done: doneCount, total: sections.length })}
      </div>
      <nav className="gop-nav" aria-label={t("sectionsNav")}>
        {sections.map((s, i) => {
          const state = i === active ? "is-active" : done[i] ? "is-done" : "";
          return (
            <button
              type="button"
              key={s.id}
              className={`gop-nav-item ${state}`}
              aria-current={i === active ? "step" : undefined}
              onClick={() => document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth", block: "start" })}
            >
              <span className="gop-nav-dot">{state === "is-done" ? <CheckOutlined rev={undefined} /> : i + 1}</span>
              {t(s.title)}
              {s.optional && <span className="gop-nav-optional">{t("optional")}</span>}
            </button>
          );
        })}
      </nav>
    </div>
  );
};

const MobileProgress: React.FC<{ step: number; sections: typeof SECTIONS }> = ({ step, sections }) => {
  const t = useT();
  return (
    <div className="gop-mprogress">
      <div className="gop-mprogress-top">
        <strong>{t("step", { n: step + 1, total: sections.length })}</strong>
        <span>{t("about2min")}</span>
      </div>
      <div className="gop-mprogress-bar">
        {sections.map((s, i) => (
          <span key={s.id} className={i <= step ? "is-on" : ""} />
        ))}
      </div>
    </div>
  );
};

// "3 of 14 answered" beside the Declarations title.
const DeclarationCount: React.FC<{ form: any; withCo: boolean }> = ({ form, withCo }) => {
  const t = useT();
  const counts =
    Form.useWatch((v: any) => {
      if (!v) return "0,0";
      const n = (prefix: string) => DECLARATION_KEYS.filter((k) => filled(v[`${prefix}declaration_${k}`])).length;
      return `${n("")},${n("co_")}`;
    }, form) || "0,0";
  const [own, co] = counts.split(",").map(Number);
  const answered = withCo ? own + co : own;
  const total = DECLARATION_KEYS.length * (withCo ? 2 : 1);
  return (
    <span className={`gop-count${answered === total ? " is-complete" : ""}`}>
      {t("answeredCount", { answered, total })}
    </span>
  );
};

// The down payment's share of the price, inside the field.
export const DownPaymentShare: React.FC<{ form: any }> = ({ form }) => {
  const share = Form.useWatch((v: any) => {
    const price = Number(v?.purchasePrice || 0);
    const down = Number(v?.downPayment || 0);
    if (!price || !down || down > price) return "";
    const pct = (down / price) * 100;
    return `${pct < 10 ? pct.toFixed(1).replace(/\.0$/, "") : Math.round(pct)}%`;
  }, form);
  return share ? <span className="gop-dp-badge">{share}</span> : null;
};

const RESEND_NOTES: Record<string, MsgKey> = {
  sent: "resendSent",
  pending: "resendPending",
  recent: "resendRecent",
  submitted: "resendSubmitted",
  error: "resendError",
};

// The language picker in the header: a native select, so a phone shows its
// own picker.
export const LanguagePicker: React.FC<{ lang: Lang; onChange: (lang: Lang) => void }> = ({ lang, onChange }) => (
  <label className="gop-lang">
    <GlobalOutlined rev={undefined} aria-hidden="true" />
    <select aria-label={translate(lang, "language")} value={lang} onChange={(e) => onChange(e.target.value as Lang)}>
      {LANGS.map((l) => (
        <option key={l.value} value={l.value}>
          {l.label}
        </option>
      ))}
    </select>
  </label>
);

const QuickIntake: React.FC = () => {
  const location = useLocation();
  const [loading, setLoading] = useState<boolean>(false);
  const [lang, setLang] = useState<Lang>(() => initialLang(location.search));
  const t: Translate = (key, vars) => translate(lang, key, vars);
  const changeLang = (next: Lang) => {
    setLang(next);
    rememberLang(next);
  };
  useEffect(() => {
    document.documentElement.lang = lang === "zh" ? "zh-CN" : lang;
  }, [lang]);
  const [form] = Form.useForm<FormValues>();
  // A validation message is built from the rules at validation time, so a
  // field already in error would keep its old-language message after a
  // switch. Re-validate just those fields once the new language has rendered.
  const validatedLang = useRef<Lang>(lang);
  useEffect(() => {
    if (validatedLang.current === lang) return;
    validatedLang.current = lang;
    const bad = form
      .getFieldsError()
      .filter((f) => f.errors.length > 0)
      .map((f) => f.name);
    if (bad.length) form.validateFields(bad).catch(() => undefined);
  }, [lang, form]);
  
  // GMCC Quick Intake is a one-shot create-only flow. Initial selection,
  // sign-in and email verification screens have been removed. The form is
  // shown immediately; on submit the borrower is registered, the application
  // is saved, and the Encompass sync runs asynchronously in the background.
  const [currentFlow, setCurrentFlow] = useState<string>("form"); // "form", "complete"
  // The legacy InitialSelection / LoginPage page-components (kept as dead code
  // for backward compat) reference these setters; we keep the state vars so
  // those components still compile, but they are never rendered or consumed.
  const [, setUserMode] = useState<string>("");
  const [isExistingUser, setIsExistingUser] = useState<boolean>(false);
  const [, setIsLoggedIn] = useState<boolean>(false);
  
  const [loanPurpose, setLoanPurpose] = useState<string>("purchase");
  const [showCoBorrower, setShowCoBorrower] = useState<boolean>(false);

  // Two entry points are supported:
  //  - New path route  /:customerId/:encompassLoid/onepage  carries the
  //    Encompass LOID directly (used by the one page app setup page link).
  //  - Legacy query route /quickintake?customerId=&loId=  carries the Zeitro
  //    loan-officer id (used by marketplace / calculators / landing pages).
  const routeParams = useParams<{ customerId?: string; encompassLoid?: string }>();
  const searchParams = new URLSearchParams(location.search);
  const customerId = routeParams.customerId || searchParams.get("customerId") || "";
  const encompassLoid = routeParams.encompassLoid || "";
  const loId = searchParams.get("loId") || "";

  const [declarationA, setDeclarationA] = useState<string>("");
  const [declarationC, setDeclarationC] = useState<string>("");
  const [declarationA1, setDeclarationA1] = useState<string>("");
  const [coDeclarationA, setCoDeclarationA] = useState<string>("");
  const [coDeclarationC, setCoDeclarationC] = useState<string>("");
  const [coDeclarationA1, setCoDeclarationA1] = useState<string>("");
  const [selectedState, setSelectedState] = useState<string>("");
  const [addressValue, setAddressValue] = useState<string>("");
  const addressValueRef = useRef<string>("");
  const [fileList, setFileList] = useState([]);
  const [coFileList, setCoFileList] = useState([]);
  const [loanId, setLoanId] = useState<string>("");
  // A loan the last submit saved but could not finalize. Submitting again
  // finalizes it instead of saving a second copy of the application.
  const unfinalizedLoanIdRef = useRef<string>("");
  const [intakeFormData, setIntakeFormData] = useState<FormValues | null>(null);
  const [loanLimits, setLoanLimits] = useState({});
  // Non-GMCC customers get their own logo/name from company customization;
  // GMCC keeps its hardcoded branding.
  const [brandLogo, setBrandLogo] = useState<string>("");
  const [brandLogoFailed, setBrandLogoFailed] = useState<boolean>(false);
  const [brandName, setBrandName] = useState<string>("");
  // 2026-09 redesign
  const isMobile = useIsMobile();
  const [step, setStep] = useState<number>(0); // phone layout: one section per step
  const [loInfo, setLoInfo] = useState<LoanOfficerInfo | null>(null);
  // Whether this customer's applications go on to Encompass. Until the server
  // says otherwise the GMCC behaviour (an activation email follows) is assumed.
  const [encompassEnabled, setEncompassEnabled] = useState<boolean>(true);
  const sections = sectionsFor(encompassEnabled);
  const [submittedEmail, setSubmittedEmail] = useState<string>("");
  const [resendToken, setResendToken] = useState<string>("");
  // The borrower portal's sign-in page, for a customer whose applications
  // stop in the LOS; finalize sends it, the thank-you screen links there.
  const [portalUrl, setPortalUrl] = useState<string>("");
  // The phone footer shows the essentials and opens the rest on request.
  const [footerOpen, setFooterOpen] = useState<boolean>(false);
  const [resendState, setResendState] = useState<string>("");

  const stillShopping = Form.useWatch("stillShopping", form);
  // "I'm still shopping": a purchase with no property picked yet. The address
  // fields fold away and nothing typed into them before the box was ticked is
  // sent.
  const noAddressYet = (v: any) => v?.loanPurpose === "purchase" && !!v?.stillShopping;
  const clearPropertyAddress = () => {
    form.setFieldsValue({
      propertyAddress: undefined,
      propertyUnit: undefined,
      propertyState: undefined,
      propertyCounty: undefined,
      propertyCity: undefined,
      propertyZip: undefined,
    });
    addressValueRef.current = "";
    setAddressValue("");
    setSelectedState("");
  };

  // The browser tab names the application and the lender, not the LOS the
  // page is served from ("Mortgage Loan Officer Page" in index.html).
  useEffect(() => {
    const lender = !customerId || customerId === "gmcc" ? "GMCC" : brandName;
    const previous = document.title;
    const name = translate(lang, "tabTitle");
    document.title = lender ? `${name} | ${lender}` : name;
    return () => {
      document.title = previous;
    };
  }, [customerId, brandName, lang]);

  // Who the borrower is applying with, for the loan officer card and the
  // header's phone number. Nothing is shown when the link names no one.
  useEffect(() => {
    if (!customerId || (!encompassLoid && !loId)) return;
    const q = new URLSearchParams({ customer_id: customerId });
    if (encompassLoid) q.set("loid", encompassLoid);
    else q.set("lo_id", loId);
    fetch(`/borrower/gmcc/onepager/loanofficer?${q.toString()}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (json) {
          setLoInfo(json);
          if (typeof json.encompass_enabled === "boolean") setEncompassEnabled(json.encompass_enabled);
        }
      })
      .catch(() => {});
  }, [customerId, encompassLoid, loId]);
  // const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
  // 获取贷款限额数据
  useEffect(() => {
    fetchLoanLimits();
  }, []);

  useEffect(() => {
    if (!customerId || customerId === "gmcc") return;
    fetch("/data/companycustomization", {
      headers: { "X-CustomerId": customerId },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (!json) return;
        if (json.CompanyName) setBrandName(json.CompanyName);
        try {
          const customization = JSON.parse(json.Customization);
          if (customization.icon) setBrandLogo(customization.icon);
        } catch (e) {
          // no customization configured; brand name alone is fine
        }
      })
      .catch((err) => console.log("Fetch company customization error:", err));
  }, [customerId]);

  // 组件卸载时清理定时器
  // useEffect(() => {
  //   return () => {
  //     if (timer) {
  //       clearTimeout(timer);
  //     }
  //   };
  // }, [timer]);

  const fetchLoanLimits = () => {
    fetch("/data/loanlimits")
      .then((res) => {
        if (!res.ok) {
          throw Error(res.statusText);
        }
        return res.json();
      })
      .then((res) => {
        setLoanLimits(res);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  // State and County options
  const getStateOptions = () => {
    let options = [];
    options.push({ value: "", label: "Select State" });
    
    if (!loanLimits) {
      return options;
    }

    let keys = Object.keys(loanLimits);
    keys.forEach((x) => {
      options.push({
        value: com.capitalize(x),
        label: com.capitalize(x),
      });
    });
    
    return options;
  };

  const getCountyOptions = (state: string) => {
    let options = [];
    options.push({ value: "", label: "Select County" });
    
    if (!state || !loanLimits) {
      return options;
    }

    let s = state.toUpperCase();
    if (state.length === 2) {
      s = com.convertAbbStateToFull(state).toUpperCase();
    }

    if (s in loanLimits) {
      let keys = Object.keys(loanLimits[s]);
      keys.forEach((x) => {
        options.push({ value: com.capitalize(x), label: com.capitalize(x) });
      });
    }
    
    return options;
  };

  // Email verification states - RE-ENABLED FOR NEW FLOW
  const [verificationCode, setVerificationCode] = useState<string>("");
  const [verified, setVerified] = useState<boolean>(false);
  const [showResult, setShowResult] = useState<boolean>(false);
  const [verifyingCode, setVerifyingCode] = useState<boolean>(false);
  const [sendingCode, setSendingCode] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [lastVerifiedCode, setLastVerifiedCode] = useState<string>(""); // 防止重复验证同一个验证码
  const [emailVerified, setEmailVerified] = useState<boolean>(false); // 跟踪邮箱是否已验证

  // SSN隐私开关状态
  const [showSSN, setShowSSN] = useState<boolean>(false);
  const [showCoSSN, setShowCoSSN] = useState<boolean>(false);

  // 格式化SSN的函数
  const formatSSN = (value: string) => {
    const ssn = value.replace(/[^\d]/g, "");
    const ssnLength = ssn.length;
    if (ssnLength < 4) return ssn;
    if (ssnLength < 6) {
      return `${ssn.slice(0, 3)}-${ssn.slice(3)}`;
    }
    return `${ssn.slice(0, 3)}-${ssn.slice(3, 5)}-${ssn.slice(5, 9)}`;
  };

  // 处理SSN显示格式的函数
  const getSSNDisplayValue = (value: string, isVisible: boolean) => {
    if (!value) return "";
    if (isVisible) {
      return formatSSN(value);
    } else {
      // 当不可见时，显示***-**-****格式
      const ssn = value.replace(/[^\d]/g, "");
      const ssnLength = ssn.length;
      if (ssnLength === 0) return "";
      if (ssnLength < 4) return "***";
      if (ssnLength < 6) return `***-${"*".repeat(ssnLength - 3)}`;
      return `***-**-${"*".repeat(Math.min(ssnLength - 5, 4))}`;
    }
  };

  // 自定义SSN输入组件
  const SSNAntdInput = ({ 
    value, 
    onChange, 
    placeholder, 
    maxLength, 
    isVisible, 
    onToggleVisibility 
  }: {
    value?: string;
    onChange?: (e: any) => void;
    placeholder?: string;
    maxLength?: number;
    isVisible: boolean;
    onToggleVisibility: () => void;
  }) => {
    const [displayValue, setDisplayValue] = useState("");
    const [isFocused, setIsFocused] = useState(false);

    // 更新显示值
    useEffect(() => {
      if (isFocused) {
        setDisplayValue(value || "");
      } else {
        setDisplayValue(getSSNDisplayValue(value || "", isVisible));
      }
    }, [value, isVisible, isFocused]);

    const handleChange = (e: any) => {
      const newValue = e.target.value;
      setDisplayValue(newValue);
      if (onChange) {
        onChange(e);
      }
    };

    const handleFocus = () => {
      setIsFocused(true);
      setDisplayValue(value || "");
    };

    const handleBlur = () => {
      setIsFocused(false);
      setDisplayValue(getSSNDisplayValue(value || "", isVisible));
    };

    return (
      <div className="gop-ssn">
        <LockOutlined rev={undefined} className="gop-ssn-lock" />
        <Input
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          maxLength={maxLength}
          inputMode="numeric"
          autoComplete="off"
        />
        <button
          type="button"
          className="gop-ssn-toggle"
          aria-label={isVisible ? t("ssnHide") : t("ssnShow")}
          onClick={onToggleVisibility}
        >
          {isVisible ? <EyeOutlined rev={undefined} /> : <EyeInvisibleOutlined rev={undefined} />}
        </button>
      </div>
    );
  };

  // 验证SSN长度的函数
  const checkSSNLength = (_, value: string) => {
    if (value && value.replace(/[^\d]/g, "").length !== 9) {
      return Promise.reject(new Error(t("badSsn")));
    }
    return Promise.resolve();
  };

  // 验证电话号码格式的函数
  const validatePhoneNumber = (_, value: string) => {
    const regex = /^\(\d{3}\) \d{3}-\d{4}$/;
    if (value && !regex.test(value)) {
      return Promise.reject(new Error(t("badPhone")));
    }
    return Promise.resolve();
  };

  const onFinish = async (values: FormValues) => {
    // 这个函数现在不会被直接调用，因为我们使用不同的按钮处理不同的提交方式
    console.log("Form submitted with values:", values);
  };

  // RE-ENABLED FOR NEW FLOW
  const codes = () => {
    return (
      <CodeVerification
        value={verificationCode}
        onChange={(code: string) => {
          console.log("--------Code onChange:", code);
          setVerificationCode(code);
          // 当输入完成时自动验证，且不是重复验证同一个验证码
          if (code.length === 4 && code !== lastVerifiedCode) {
            // console.log("--------Code onCompleted:", code);
            verifyCode(code);
          }
        }}
      />
    );
  };

  // RE-ENABLED FOR NEW FLOW
  const sendVerificationCode = async (email: string) => {
    setSendingCode(true);
    try {
      const response = await fetch("/los/generateonetimepasscode", {
        cache: "no-cache",
        method: "POST",
        body: JSON.stringify({
          Email: email,
        }),
        headers: {
          "Content-Type": "application/json",
          Cache: "no-cache",
        },
      });

      if (response.status !== 200) {
        console.log(
          "Looks like there was a problem. Status Code: " + response.status
        );
        message.error("Failed to send verification code, please try again");
        return;
      }

      message.success("Verification code sent to your email");
    } catch (error) {
      console.log(error);
      message.error("Failed to send verification code, please try again");
    } finally {
      setSendingCode(false);
    }
  };

  // RE-ENABLED FOR NEW FLOW
  const verifyCode = async (code: string) => {
    setVerifyingCode(true);
    setLastVerifiedCode(code); // 记录已验证的验证码
    try {
      const response = await fetch("/los/verifycodeandsignin", {
        cache: "no-cache",
        method: "POST",
        body: JSON.stringify({
          Email: email,
          Code: code,
        //   loId: loId,
        //   CustomerID: new URLSearchParams(location.search).get("customerId") || "",
        }),
        headers: {
          "Content-Type": "application/json",
          Cache: "no-cache",
        },
      });

      if (response.status !== 200) {
        console.log(
          "Looks like there was a problem. Status Code: " + response.status
        );
        setShowResult(true);
        setVerified(false);
        console.log("set code to empty");
        setVerificationCode("");
        message.error("Verification failed, please try again");
        return;
      }

      const result = await response.json();

      if (result.Status !== "OK") {
        console.log(result);
        setShowResult(true);
        setVerified(false);
        console.log("set code to empty");
        setVerificationCode("");
        message.error("Invalid or expired verification code");
        return;
      } else {
        // Email verification is no longer part of the create-only flow,
        // but the helper functions are still present for legacy callers.
        setVerified(true);
        setEmailVerified(true);
        setShowResult(true);
        message.success("Email verification successful!");
        console.log("set code to empty");
        setVerificationCode("");
        await saveAllData(intakeFormData);
        setTimeout(() => {
          setCurrentFlow("form");
        }, 2000);
      }
    } catch (error) {
      console.log(error);
      message.error("Verification failed, please try again");
    } finally {
      setVerifyingCode(false);
    }
  };

  // RE-ENABLED FOR NEW FLOW
  const handleResendCode = () => {
    sendVerificationCode(email);
  };

  const handleEditEmail = () => {
    // 如果已经验证通过，不允许编辑邮箱
    if (emailVerified) {
      message.warning("Email cannot be modified after verification");
      return;
    }
    
    setVerified(false);
    setShowResult(false);
    console.log("set code to empty");
    setVerificationCode("");
    setLastVerifiedCode(""); // 重置已验证的验证码
  };

  // 文件上传相关函数
  const isHeicOrHeif = (fileName) => {
    return /\.(heic|heif)$/i.test(fileName);
  };

  const makeDraggerProps = (list, setList) => ({
    fileList: list,
    multiple: true,
    showUploadList: false,
    // Files go up with the application (uploadDocuments, after the loan
    // exists). Without this, antd also POSTed every dropped file straight away
    // to the page's own URL -- a request nothing handles, carrying the
    // borrower's documents.
    customRequest: ({ onSuccess }) => onSuccess?.("ok"),
    onChange(info) {
      setList([...info.fileList]);
    },
    onDrop(e) {},
    beforeUpload: async (file) => {
      if (isHeicOrHeif(file?.name?.toLowerCase())) {
        message.warning(t("onlyImagesPdf"));
        return Upload.LIST_IGNORE;
      } else {
        // const allowedFileTypes = ["image/jpeg", "image/png", "application/pdf"];
        // const fileType = file.type;
        // const isAllowed = allowedFileTypes.includes(fileType);
        // if (!isAllowed) {
        //   message.warning("You can only upload image and PDF files.");
        //   return Upload.LIST_IGNORE;
        // }
      }
      return true;
    },
  });

  const draggerProps = makeDraggerProps(fileList, setFileList);
  const coDraggerProps = makeDraggerProps(coFileList, setCoFileList);

  const handleDeleteDoc = (index) => {
    const updatedFile = [...fileList];
    updatedFile.splice(index, 1);
    setFileList(updatedFile);
  };

  const handleDeleteCoDoc = (index) => {
    const updatedFile = [...coFileList];
    updatedFile.splice(index, 1);
    setCoFileList(updatedFile);
  };

  // 新增：保存所有数据的函数
  // Resolves true only once the application is saved and handed to the
  // Encompass sync; every failure has already been shown to the borrower.
  const saveAllData = async (values: FormValues): Promise<boolean> => {
    console.log("saveAllData values:", values);
    if (unfinalizedLoanIdRef.current) {
      return finalizeApplication(unfinalizedLoanIdRef.current);
    }
    try {
      // 补充地址相关字段
      const addressFields = noAddressYet(values)
        ? { propertyAddress: "", propertyUnit: "", propertyState: "", propertyCounty: "", propertyCity: "", propertyZip: "" }
        : {
            propertyAddress: addressValueRef.current || values.propertyAddress || "",
            propertyState: selectedState || values.propertyState || "",
            propertyCounty: values.propertyCounty || "",
            propertyCity: values.propertyCity || "",
            propertyZip: values.propertyZip || "",
          };
      const allValues = { ...values, ...addressFields };

      // 构建完整的数据
      let allData = {
        // Basic borrower information
        firstName: allValues.firstName,
        lastName: allValues.lastName,
        email: allValues.email,
        phone: allValues.phone,
        ssn: allValues.ssn,
        ficoScore: Number(allValues.ficoScore || ""),
        hasCoBorrower: !!allValues.hasCoBorrower,

        // Co-borrower information
        coFirstName: allValues.coFirstName,
        coLastName: allValues.coLastName,
        coEmail: allValues.coEmail,
        coPhone: allValues.coPhone,
        coSsn: allValues.coSsn,
        coFicoScore: Number(allValues.coFicoScore || ""),

        // Loan information
        loanPurpose: allValues.loanPurpose,
        loanAmount: loanAmountFor(allValues),
        purchasePrice: allValues.loanPurpose === "purchase" ? Number(allValues.purchasePrice || "") : 0,
        downPayment: Number(allValues.downPayment || ""),
        isFirstTimeBuyer: allValues.isFirstTimeBuyer,
        remainingBalance: Number(allValues.remainingBalance || ""),
        currentInterestRate: Number(allValues.currentInterestRate || ""),
        hasSecondMortgage: allValues.hasSecondMortgage === "yes" ? 1 : 0,
        borrowAdditionalCash: Number(allValues.borrowAdditionalCash || ""),

        // Property information
        propertyAddress: allValues.propertyAddress,
        propertyUnit: allValues.propertyUnit,
        propertyState: allValues.propertyState,
        propertyCounty: allValues.propertyCounty,
        propertyCity: allValues.propertyCity,
        propertyZip: allValues.propertyZip,
        propertyValue: Number(allValues.propertyValue || ""),
        propertyUse: allValues.propertyUse,

        // Declarations
        declaration_A: allValues.declaration_A,
        declaration_B: allValues.declaration_B,
        declaration_C: allValues.declaration_C,
        declaration_D1: allValues.declaration_D1,
        declaration_D2: allValues.declaration_D2,
        declaration_E: allValues.declaration_E,
        declaration_F: allValues.declaration_F,
        declaration_G: allValues.declaration_G,
        declaration_H: allValues.declaration_H,
        declaration_I: allValues.declaration_I,
        declaration_J: allValues.declaration_J,
        declaration_K: allValues.declaration_K,
        declaration_L: allValues.declaration_L,
        declaration_M: allValues.declaration_M,

        // Additional declarations
        declaration_A1: allValues.declaration_A1,
        declaration_A1_Type: allValues.declaration_A1_Type,
        declaration_A1_Status: allValues.declaration_A1_Status,
        declaration_C1: Number(allValues.declaration_C1 || ""),

        // Co-borrower declarations (only present when hasCoBorrower)
        co_declaration_A: allValues.co_declaration_A,
        co_declaration_B: allValues.co_declaration_B,
        co_declaration_C: allValues.co_declaration_C,
        co_declaration_D1: allValues.co_declaration_D1,
        co_declaration_D2: allValues.co_declaration_D2,
        co_declaration_E: allValues.co_declaration_E,
        co_declaration_F: allValues.co_declaration_F,
        co_declaration_G: allValues.co_declaration_G,
        co_declaration_H: allValues.co_declaration_H,
        co_declaration_I: allValues.co_declaration_I,
        co_declaration_J: allValues.co_declaration_J,
        co_declaration_K: allValues.co_declaration_K,
        co_declaration_L: allValues.co_declaration_L,
        co_declaration_M: allValues.co_declaration_M,
        co_declaration_A1: allValues.co_declaration_A1,
        co_declaration_A1_Type: allValues.co_declaration_A1_Type,
        co_declaration_A1_Status: allValues.co_declaration_A1_Status,
        co_declaration_C1: Number(allValues.co_declaration_C1 || ""),

        // Customer ID from URL
        customerId: customerId,
        // Loan Officer ID from URL (legacy query route). Empty on the new
        // path route, where the backend resolves the LO from encompassLoid.
        loanOfficerId: loId,
        // Encompass LOID from the path route; backend reverse-maps it to the
        // Zeitro loan officer to set loanofficer_id.
        encompassLoid: encompassLoid,
      };

      console.log("All data to save:", allData);

      // 保存基本信息
      const response = await fetch("/borrower/gmcc/quickapplication", {
        cache: "no-cache",
        method: "POST",
        body: JSON.stringify(allData),
        headers: {
          "Content-Type": "application/json",
          Cache: "no-cache",
        },
      });

      if (response.status !== 200) {
        console.log("Save data failed. Status Code: " + response.status);
        message.error(t("saveFailed"));
        return false;
      }

      const result = await response.json();

      if (result.status === "OK" && result.loan_id) {
        setLoanId(result.loan_id);
        console.log("Data saved successfully, LoanID:", result.loan_id);

        if (fileList.length > 0 || coFileList.length > 0) {
          await uploadDocuments(result.loan_id, "");
        }
        unfinalizedLoanIdRef.current = result.loan_id;
        return finalizeApplication(result.loan_id);
      } else {
        console.log("Save data failed:", result);
        message.error(t("saveFailed"));
        return false;
      }
    } catch (error) {
      console.log("Save data error:", error);
      message.error(t("saveFailed"));
      return false;
    }
  };

  // Finalize is asynchronous: the backend enqueues a job and returns
  // 202 Accepted immediately. The Encompass sync (CreateLoan, MISMO,
  // attachments, Consumer Connect invitation + email) happens in the
  // background worker, which retries transient failures (lock conflict,
  // 429, 5xx) for up to 24h before notifying the LO via email.
  const finalizeApplication = async (savedLoanId: string): Promise<boolean> => {
    try {
      const finalizeRes = await fetch("/borrower/gmcc/quickapplication/finalize", {
        cache: "no-cache",
        method: "POST",
        body: JSON.stringify({ loan_id: savedLoanId, customer_id: customerId }),
        headers: { "Content-Type": "application/json", "Cache": "no-cache" },
      });
      if (finalizeRes.status !== 202 && finalizeRes.status !== 200) {
        const errBody = await finalizeRes.json().catch(() => ({}));
        message.error(errBody.error_message || errBody.error || t("submitFailed"));
        return false;
      }
      // Lets the thank-you screen's "Resend email" ask for the link again.
      const accepted = await finalizeRes.json().catch(() => ({}));
      if (accepted?.resend_token) setResendToken(accepted.resend_token);
      if (typeof accepted?.encompass_enabled === "boolean") setEncompassEnabled(accepted.encompass_enabled);
      if (typeof accepted?.portal_url === "string") setPortalUrl(accepted.portal_url);
      unfinalizedLoanIdRef.current = "";
      return true;
    } catch (error) {
      console.log("Finalize error:", error);
      message.error(t("submitFailed"));
      return false;
    }
  };

  // 新增：上传文档的函数
  const uploadDocuments = async (loanId: string, encompassLoanId: string) => {
    try {
      const formData = new FormData();
      fileList.forEach((item) => {
        formData.append(`upload`, item.originFileObj);
      });
      coFileList.forEach((item) => {
        formData.append(`upload_coborrower`, item.originFileObj);
      });

      const response = await fetch("/docs/gmcc/quickapplicationdocupload", {
        cache: "no-cache",
        method: "POST",
        body: formData,
        headers: {
          Cache: "no-cache",
          "X-LOAN": loanId,
          "X-ENCOMPASS-LOAN": encompassLoanId,
          "X-CustomerID": customerId,
          "X-LOID": loId,
        },
      });

      if (response.status !== 200) {
        console.log("Upload documents failed. Status Code: " + response.status);
        message.error(t("uploadFailed"));
        return;
      }

      const result = await response.json();

      if (result.Status === "OK") {
        console.log("Documents uploaded successfully");
      } else {
        console.log("Upload documents failed:", result);
        message.error(t("uploadFailed"));
      }
    } catch (error) {
      console.log("Upload documents error:", error);
      message.error(t("uploadFailed"));
    }
  };

  // 新增：加载现有用户数据
  const loadExistingUserData = async (userEmail: string) => {
    try {
      const response = await fetch(`/borrower/getuserdata?email=${userEmail}`, {
        cache: "no-cache",
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Cache: "no-cache",
        },
      });

      if (response.status === 200) {
        const userData = await response.json();
        if (userData.status === "OK" && userData.data) {
          // 填充表单数据
          form.setFieldsValue(userData.data);
          message.success("Your existing data has been loaded");
        }
      }
    } catch (error) {
      console.log("Load user data error:", error);
      message.warning("Could not load existing data, please fill the form manually");
    }
  };

  // 新增：处理新用户注册
  const handleSignUp = async (values: FormValues) => {
    console.log("handleSignUp values:", values);
    try {
      // 先发送验证码
      await sendVerificationCode(values.email);
      setEmail(values.email);
      setCurrentFlow("verify");
      setIntakeFormData(values);
    } catch (error) {
      message.error("Failed to send verification code, please try again");
    }
  };

  // 新增：处理一次性提交
  const handleOneTimeSubmission = async (values: FormValues) => {
    setLoading(true);
    try {
      if (!(await saveAllData(values))) return;
      message.success(t("submitted"));
      setSubmittedEmail(values.email || "");
      setCurrentFlow("complete");
      window.scrollTo({ top: 0 });
    } catch (error) {
      message.error(t("submissionFailed"));
    } finally {
      setLoading(false);
    }
  };

  // 新增：处理注册后的提交
  const handleSignUpSubmission = async (values: FormValues) => {
    setLoading(true);
    try {
      // 创建borrower账户并保存数据
      if (!(await saveAllData(values))) return;
      message.success("Account created and application submitted successfully!");
      setCurrentFlow("complete");
    } catch (error) {
      message.error("Failed to create account and submit application, please try again");
    } finally {
      setLoading(false);
    }
  };

  // 新增：处理现有用户更新
  const handleUpdateSubmission = async (values: FormValues) => {
    setLoading(true);
    try {
      // 更新现有用户数据
      await updateExistingUserData(values);
      message.success("Your information has been updated successfully!");
      setCurrentFlow("complete");
    } catch (error) {
      message.error("Failed to update information, please try again");
    } finally {
      setLoading(false);
    }
  };

  // 新增：更新现有用户数据
  const updateExistingUserData = async (values: FormValues) => {
    try {
      // 补充地址相关字段
      const addressFields = noAddressYet(values)
        ? { propertyAddress: "", propertyUnit: "", propertyState: "", propertyCounty: "", propertyCity: "", propertyZip: "" }
        : {
            propertyAddress: addressValueRef.current || values.propertyAddress || "",
            propertyState: selectedState || values.propertyState || "",
            propertyCounty: values.propertyCounty || "",
            propertyCity: values.propertyCity || "",
            propertyZip: values.propertyZip || "",
          };
      const allValues = { ...values, ...addressFields };

      // 构建更新数据
      let updateData = {
        email: email, // 使用已验证的邮箱
        // 包含所有表单数据
        ...allValues,
        customerId: customerId,
        loanOfficerId: loId,
      };

      console.log("Update data:", updateData);

      const response = await fetch("/borrower/updateuserdata", {
        cache: "no-cache",
        method: "POST",
        body: JSON.stringify(updateData),
        headers: {
          "Content-Type": "application/json",
          Cache: "no-cache",
        },
      });

      if (response.status !== 200) {
        console.log("Update data failed. Status Code: " + response.status);
        message.error("Failed to update data, please try again");
        return;
      }

      const result = await response.json();

      if (result.status === "OK") {
        console.log("Data updated successfully");
        
        // 如果有文件，上传文件
        if ((fileList.length > 0 || coFileList.length > 0) && result.loan_id) {
          await uploadDocuments(result.loan_id, result.encompass_loan_id);
        }
      } else {
        console.log("Update data failed:", result);
        message.error("Failed to update data, please try again");
      }
    } catch (error) {
      console.log("Update data error:", error);
      message.error("Failed to update data, please try again");
    }
  };

  const gmcclogo = () => {
    if (customerId && customerId !== "gmcc") {
      if (brandLogo && !brandLogoFailed) {
        // The icon is base64 when it was uploaded in LOS, but some companies
        // hold a hosted image URL there (zeitro: an S3 link). Wrapping a URL
        // in a data: URL drew a broken image showing its alt text.
        const src = /^(https?:|data:|\/)/.test(brandLogo) ? brandLogo : `data:image/png;base64, ${brandLogo}`;
        return (
          <div>
            <img
              src={src}
              alt={brandName || "Company Logo"}
              style={{ height: 40, maxWidth: 250, objectFit: "contain" }}
              onError={() => setBrandLogoFailed(true)}
            />
          </div>
        );
      }
      if (brandName) {
        return (
          <div style={{ fontSize: 24, fontWeight: 600, color: "#1F3988" }}>
            {brandName}
          </div>
        );
      }
      return <div />;
    }
    return (
      <div>
        <img
          src="/images/lendersLogo/GMCC_logoWithText.svg"
          alt="Company Logo"
          width="187"
          height="40"
        />
      </div>
    );
  };

  // 新增：初始选择页面组件
  const InitialSelection = () => {
    return (
      <div style={{ textAlign: "center", padding: "40px 20px" }}>
        <Title level={2} style={{ marginBottom: "40px", color: "#1F3988" }}>
          Start your application with GMCC
        </Title>
        
        <div style={{ marginBottom: "40px" }}>
          <p style={{ fontSize: "16px", color: "#666", marginBottom: "30px" }}>
            Please choose how you would like to proceed:
          </p>
        </div>

        <div style={{ display: "flex", justifyContent: "center", gap: "30px", flexWrap: "wrap" }}>
          <Card
            hoverable
            style={{ 
              width: "300px", 
              textAlign: "center",
              border: "2px solid #1F3988",
              borderRadius: "8px",
              height: "200px",
              display: "flex",
              flexDirection: "column"
            }}
            onClick={() => {
              setUserMode("new");
              setCurrentFlow("form");
            }}
          >
            <div style={{ padding: "20px", flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
              <div>
                <Title level={4} style={{ color: "#1F3988", marginBottom: "15px" }}>
                  Create New Application
                </Title>
                <p style={{ color: "#666", marginBottom: "20px" }}>
                  Start a new loan application
                </p>
              </div>
              {/* <Button 
                type="primary" 
                size="large"
                style={{
                  backgroundColor: "#1F3988",
                  borderColor: "#1F3988",
                  minWidth: "150px"
                }}
              >
                Get Started
              </Button> */}
            </div>
          </Card>

          <Card
            hoverable
            style={{ 
              width: "300px", 
              textAlign: "center",
              border: "2px solid #1F3988",
              borderRadius: "8px",
              height: "200px",
              display: "flex",
              flexDirection: "column"
            }}
            onClick={() => {
              setUserMode("existing");
              setIsExistingUser(true);
              setCurrentFlow("login");
            }}
          >
            <div style={{ padding: "20px", flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
              <div>
                <Title level={4} style={{ color: "#1F3988", marginBottom: "15px" }}>
                  Update Existing Application
                </Title>
                <p style={{ color: "#666", marginBottom: "20px" }}>
                  Sign in to update your existing application
                </p>
              </div>
              {/* <Button 
                type="primary" 
                size="large"
                style={{
                  backgroundColor: "#1F3988",
                  borderColor: "#1F3988",
                  minWidth: "150px"
                }}
              >
                Sign In
              </Button> */}
            </div>
          </Card>
        </div>
      </div>
    );
  };

  // 新增：登录页面组件
  const LoginPage = () => {
    const [loginEmail, setLoginEmail] = useState<string>("");

    const handleLoginSubmit = async () => {
      if (!loginEmail) {
        message.error("Please enter your email address");
        return;
      }
      
      try {
        await sendVerificationCode(loginEmail);
        setEmail(loginEmail);
        setCurrentFlow("verify");
      } catch (error) {
        message.error("Failed to send verification code, please try again");
      }
    };

    return (
      <div style={{ textAlign: "center", padding: "40px 20px" }}>
        <Title level={2} style={{ marginBottom: "40px", color: "#1F3988" }}>
          Sign In to Your Account
        </Title>
        
        <div style={{ maxWidth: "400px", margin: "0 auto" }}>
          <Form layout="vertical">
            <Form.Item
              label="Email Address"
              rules={[
                { required: true, message: "Please enter your email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input
                placeholder="Enter your email address"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                size="large"
              />
            </Form.Item>
            
            <Form.Item style={{ marginTop: "30px" }}>
              <Button
                type="primary"
                size="large"
                onClick={handleLoginSubmit}
                loading={sendingCode}
                style={{
                  backgroundColor: "#1F3988",
                  borderColor: "#1F3988",
                  minWidth: "200px"
                }}
              >
                Send Verification Code
              </Button>
            </Form.Item>
          </Form>
          
          <div style={{ marginTop: "20px" }}>
            <Button 
              type="link" 
              onClick={() => {
                setCurrentFlow("initial");
                setIsExistingUser(false);
              }}
            >
              Back to Selection
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // 新增：邮箱验证页面组件
  const EmailVerificationPage = () => {
    const [isEditingEmail, setIsEditingEmail] = useState<boolean>(false);
    const [newEmail, setNewEmail] = useState<string>("");

    const showErrorMessage = () => {
      return (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div
            style={{
              backgroundColor: "#FFE6BF",
              borderRadius: "4px",
              padding: "10px",
              marginTop: "15px",
              maxWidth: "350px",
            }}
          >
            <img
              src="/images/warningSignYellow.png"
              style={{ width: "18px", height: "18px", marginRight: "10px" }}
            />
            <span>The code is incorrect or expired.</span>
          </div>
        </div>
      );
    };

    const handleEmailChange = async () => {
      if (!newEmail) {
        message.error("Please enter a new email address");
        return;
      }
      
      if (newEmail === email) {
        message.warning("Please enter a different email address");
        return;
      }

      try {
        // 更新表单中的邮箱
        form.setFieldValue("email", newEmail);
        
        // 发送验证码到新邮箱
        await sendVerificationCode(newEmail);
        
        // 更新状态
        setEmail(newEmail);
        setNewEmail("");
        setIsEditingEmail(false);
        setVerified(false);
        setShowResult(false);
        console.log("set code to empty");
        setVerificationCode("");
        setLastVerifiedCode("");
        
        message.success("Verification code sent to new email address");
      } catch (error) {
        message.error("Failed to send verification code to new email, please try again");
      }
    };

    const handleCancelEmailEdit = () => {
      setNewEmail("");
      setIsEditingEmail(false);
    };

    if (showResult && verified) {
      return (
        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <img
            src="/images/checkEmail2.png"
            style={{ width: "80px", height: "78px" }}
          />
          <div
            style={{
              fontFamily: "Inter",
              fontStyle: "normal",
              fontWeight: 700,
              fontSize: "26px",
              lineHeight: "normal",
              marginTop: "23px",
              marginBottom: "6px",
            }}
          >
            Verified!
          </div>
          <div
            style={{
              color: "#6E6E70",
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "20px",
              marginTop: "10px",
              marginBottom: "12px",
            }}
          >
            <div>Thank you for verifying your email</div>
            <div>Redirecting to application form...</div>
          </div>
        </div>
      );
    }

    return (
      <div className="quick-intake-container" style={{ textAlign: "center", marginBottom: "20px" }}>
        <div>
          <img
            src="/images/checkEmail2.png"
            style={{ width: 66, height: "auto", marginBottom: "23px" }}
          />
        </div>
        <div
          style={{
            color: "#222",
            fontSize: 26,
            fontFamily: "Playfair Display",
            lineHeight: "35px",
            fontWeight: 700,
            marginBottom: "10px",
          }}
        >
          We need to verify your email
        </div>

        {email && (
          <div
            style={{
              margin: "6px auto 18px",
              color: "#6E6E70",
              lineHeight: "20px",
            }}
          >
            {!isEditingEmail ? (
              <div>
                <div>
                  We sent a code to{" "}
                  <span style={{ fontWeight: 600, color: "#222" }}>{email}</span>
                  {!emailVerified && (
                    <a
                      style={{
                        color: "#325CEB",
                        marginLeft: 8,
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                      onClick={() => setIsEditingEmail(true)}
                    >
                      Edit
                    </a>
                  )}
                </div>
                <div>Enter the code to activate your account</div>
              </div>
            ) : (
              <div style={{ maxWidth: "400px", margin: "0 auto" }}>
                <div style={{ marginBottom: "15px", color: "#222", fontWeight: 500 }}>
                  Change Email Address
                </div>
                <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "10px" }}>
                  <Input
                    placeholder="Enter new email address"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    style={{ flex: 1 }}
                    onPressEnter={handleEmailChange}
                  />
                  <Button
                    type="primary"
                    size="small"
                    onClick={handleEmailChange}
                    style={{
                      backgroundColor: "#1F3988",
                      borderColor: "#1F3988",
                    }}
                  >
                    Update
                  </Button>
                  <Button
                    size="small"
                    onClick={handleCancelEmailEdit}
                  >
                    Cancel
                  </Button>
                </div>
                <div style={{ fontSize: "12px", color: "#999" }}>
                  A new verification code will be sent to the new email address
                </div>
              </div>
            )}
          </div>
        )}

        {!isEditingEmail && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "20px 0 0 10px",
            }}
          >
            {verifyingCode || sendingCode ? (
              <div style={{ padding: "20px" }}>
                {/* @ts-ignore */}
                <Spinner animation="border" size="lg" />
              </div>
            ) : (
              codes()
            )}
          </div>
        )}

        {!isEditingEmail && showResult && !verified && showErrorMessage()}

        {!isEditingEmail && (
          <div
            style={{
              fontSize: 16,
              padding: "8px",
              fontWeight: 400,
              color: "#6e6e70",
            }}
          >
            Didn't receive the email?
            <a
              style={{
                color: "#325CEB",
                textDecoration: "underline",
                cursor: "pointer",
                marginLeft: "8px",
              }}
              onClick={handleResendCode}
            >
              Resend
            </a>
            {showResult && !verified && (
              <>
                {" "}
                or{" "}
                <a
                  style={{
                    color: "#325CEB",
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    console.log("set code to empty");
                    setVerificationCode("");
                    setLastVerifiedCode("");
                    setShowResult(false);
                  }}
                >
                  Try again
                </a>
              </>
            )}
          </div>
        )}
        
        <div style={{ marginTop: "20px" }}>
          <Button 
            type="link" 
            onClick={() => {
              setCurrentFlow("initial");
              setIsExistingUser(false);
              setEmail("");
              setVerified(false);
              setShowResult(false);
            }}
          >
            Back to Selection
          </Button>
        </div>
      </div>
    );
  };

  // SubmitSelection组件已删除，提交选择直接集成到表单中

  // 新增：完成页面组件
  const resendEmail = async () => {
    if (!resendToken || resendState === "sending") return;
    setResendState("sending");
    try {
      const res = await fetch("/borrower/gmcc/quickapplication/resend", {
        method: "POST",
        cache: "no-cache",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: resendToken }),
      });
      const json = res.ok ? await res.json().catch(() => null) : null;
      const status = json?.status;
      setResendState(["sent", "pending", "recent", "submitted"].includes(status) ? status : "error");
    } catch (error) {
      setResendState("error");
    }
  };

  const footerCfg = CUSTOMER_FOOTERS[customerId];
  // On a phone only the links, the company line and the contact line show;
  // everything in .gop-sf-more opens with the "More" control. On desktop
  // the wrappers are display: contents and the control is hidden.
  const siteFooter = footerCfg ? (
    <footer
      className={`gop-site-footer${currentFlow === "form" ? " is-form" : ""}${footerOpen ? " is-open" : ""}`}
    >
      <div className="gop-sf-inner">
        <div className="gop-sf-main">
          <nav className="gop-sf-links" aria-label={t("legalLinks")}>
            {footerCfg.links.map((l) => (
              <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer">
                {t(l.label)}
              </a>
            ))}
          </nav>
          <div className="gop-sf-company">{footerCfg.company}</div>
          <div className="gop-sf-line">
            <span>NMLS #{footerCfg.nmls}</span>
            {footerCfg.badges.map((b) => (
              <span key={b}>
                <span className="gop-sf-sep">|</span>
                {b}
              </span>
            ))}
          </div>
          <div className="gop-sf-more">
            <div className="gop-sf-line">
              <a href={footerCfg.consumerAccess} target="_blank" rel="noopener noreferrer">
                {t("nmlsConsumerAccess")}
              </a>
            </div>
            {footerCfg.licensing.map((line) => (
              <div key={line} className="gop-sf-line">
                {line}
              </div>
            ))}
            <div className="gop-sf-line">
              {t(isMobile ? "complaintsShort" : "complaintsLine")}{" "}
              <a href={`mailto:${footerCfg.complaintsEmail}?subject=Complaint`}>{footerCfg.complaintsEmail}</a>
            </div>
          </div>
        </div>
        <div className="gop-sf-side">
          <div className="gop-sf-more">
            <div className="gop-sf-line">{footerCfg.address}</div>
          </div>
          <div className="gop-sf-line">
            <a href={`tel:${footerCfg.phone.replace(/[^0-9+]/g, "")}`}>{footerCfg.phone}</a>
            <span className="gop-sf-sep">|</span>
            <a href={`mailto:${footerCfg.email}`}>{footerCfg.email}</a>
          </div>
          <div className="gop-sf-social">
            {footerCfg.social.map((sl) => (
              <a
                key={sl.href}
                href={sl.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={sl.kind === "facebook" ? "Facebook" : "LinkedIn"}
              >
                {sl.kind === "facebook" ? <FacebookOutlined rev={undefined} /> : <LinkedinOutlined rev={undefined} />}
              </a>
            ))}
            <a href={`mailto:${footerCfg.email}`} aria-label={t("email")}>
              <MailOutlined rev={undefined} />
            </a>
            {footerCfg.equalHousingLogo && (
              <img className="gop-sf-ehl" src={footerCfg.equalHousingLogo} alt="Equal Housing Opportunity" />
            )}
          </div>
        </div>
      </div>
      <button
        type="button"
        className="gop-sf-toggle"
        aria-expanded={footerOpen}
        onClick={() => setFooterOpen((open) => !open)}
      >
        {footerOpen ? t("footerLess") : t("footerMore")}
        {footerOpen ? <UpOutlined rev={undefined} /> : <DownOutlined rev={undefined} />}
      </button>
    </footer>
  ) : null;

  // The thank-you screen: what is done, then what the borrower does next.
  // The sentences are the screen's original copy.
  const completePage = () => (
    <div className="gop-done-wrap">
      <div className="gop-done">
        <div className="gop-done-head">
          <span className="gop-done-badge">
            <span>
              <CheckOutlined rev={undefined} />
            </span>
          </span>
          <div className="gop-done-title" role="heading" aria-level={1}>
            {t("doneTitle")}
          </div>
          <p className="gop-done-lede">{t("doneLede")}</p>
        </div>
        {/* A customer on Encompass: the application's progress lives in
            Consumer Connect, which the activation email opens. A customer
            without one: the borrower portal is where the application is
            followed, so the screen hands over its sign-in link instead of
            a timeline whose only entry is "submitted". */}
        {encompassEnabled ? (
        <div className="gop-done-body">
          <div className="gop-eyebrow">{t("yourProgress")}</div>
          <div className="gop-tl">
            <div className="gop-tl-rail">
              <span className="gop-tl-dot">
                <CheckOutlined rev={undefined} />
              </span>
              <span className="gop-tl-line" />
            </div>
            <div className="gop-tl-done">
              <span className="gop-tl-title">{t("appSubmitted")}</span>
              <span className="gop-pill gop-pill-done">{t("done")}</span>
            </div>
          </div>
          <div className="gop-tl">
            <div className="gop-tl-rail">
              <span className="gop-tl-dot is-next">
                <MailOutlined rev={undefined} />
              </span>
            </div>
            <div className="gop-next">
              <div className="gop-next-head">
                <span className="gop-next-title">{t("nextStepEmail")}</span>
                <span className="gop-pill gop-pill-action">{t("actionNeeded")}</span>
              </div>
              <div className="gop-next-text">
                {t("nextStepText")}
              </div>
              {submittedEmail && (
                <div className="gop-sent-to">
                  <SendOutlined rev={undefined} />
                  <span>
                    {t("sentTo")} <strong>{submittedEmail}</strong>
                  </span>
                </div>
              )}
              <div className="gop-next-foot">
                <span className="gop-next-help">{t("checkSpam")}</span>
                {resendToken && (
                  <button
                    type="button"
                    className="gop-btn-ghost"
                    onClick={resendEmail}
                    disabled={resendState === "sending"}
                  >
                    <RedoOutlined rev={undefined} />
                    {resendState === "sending" ? t("sending") : t("resendEmail")}
                  </button>
                )}
              </div>
              {RESEND_NOTES[resendState] && (
                <div className={`gop-resend-note${resendState === "sent" ? "" : " is-warn"}`} role="status">
                  {t(RESEND_NOTES[resendState])}
                </div>
              )}
            </div>
          </div>
        </div>
        ) : portalUrl ? (
        <div className="gop-done-body">
          <div className="gop-portal">
            <div className="gop-next-head">
              <span className="gop-next-title">{t("portalTitle")}</span>
            </div>
            <div className="gop-next-text">{t("portalText")}</div>
            {submittedEmail && (
              <div className="gop-sent-to">
                <MailOutlined rev={undefined} />
                <span>
                  {t("portalSignInAs")} <strong>{submittedEmail}</strong>
                </span>
              </div>
            )}
            <a className="gop-btn-primary gop-portal-cta" href={portalUrl}>
              {t("openPortal")}
            </a>
          </div>
        </div>
        ) : null}
      </div>
    </div>
  );

//   const log_svg = () => {
//     return (
//       <svg
//         xmlns="http://www.w3.org/2000/svg"
//         width="187"
//         height="40"
//         viewBox="0 0 187 40"
//         fill="none"
//       >
//         <path
//           d="M110.434 8.69106C111.594 8.69106 112.571 8.28811 113.375 7.48221C114.175 6.67631 114.574 5.68592 114.574 4.51591C114.574 3.3459 114.175 2.35551 113.375 1.54961C112.576 0.743712 111.594 0.335907 110.434 0.335907C109.273 0.335907 108.296 0.738857 107.492 1.54961C106.692 2.35066 106.293 3.3459 106.293 4.51591C106.293 5.68592 106.692 6.67145 107.492 7.48221C108.291 8.28325 109.273 8.69106 110.434 8.69106Z"
//           fill="#081265"
//         />
//         <path
//           d="M113.963 10.7338H106.905V36.2508H113.963V10.7338Z"
//           fill="#081265"
//         />
//         <path
//           d="M128.309 15.8564V27.2895C128.309 28.2702 128.559 29.0373 129.064 29.5907C129.565 30.1442 130.157 30.4209 130.851 30.4209C132.165 30.4209 133.277 29.8189 134.187 28.6198L136.821 33.6008C134.625 35.6253 132.31 36.6351 129.883 36.6351C127.456 36.6351 125.396 35.834 123.72 34.2174C122.045 32.6056 121.207 30.4063 121.207 27.6245V15.8613H118.241V10.7395H121.207V3.09796H128.309V10.7346H134.423V15.8564H128.309Z"
//           fill="#081265"
//         />
//         <path
//           d="M153.145 16.9858C151.046 16.9858 149.476 17.7383 148.441 19.2384C147.406 20.7386 146.89 22.7242 146.89 25.1904V36.24H139.832V10.7279H146.89V14.0972C147.796 13.0534 148.937 12.1649 150.299 11.4416C151.662 10.7134 153.048 10.3347 154.459 10.3007L154.507 16.9858H153.145Z"
//           fill="#081265"
//         />
//         <path
//           d="M67.8096 10.779H54.9884L51.0435 16.7458H63.8646L51.0435 36.1377H72.2476L76.1926 30.171H63.3715L76.1926 10.779H67.8096Z"
//           fill="#081265"
//         />
//         <path
//           fill-rule="evenodd"
//           clip-rule="evenodd"
//           d="M169.632 36.633C176.849 36.633 182.7 30.7337 182.7 23.4564C182.7 16.1791 176.849 10.2798 169.632 10.2798C162.415 10.2798 156.564 16.1791 156.564 23.4564C156.564 30.7337 162.415 36.633 169.632 36.633ZM169.632 29.9204C173.173 29.9204 176.043 27.0263 176.043 23.4564C176.043 19.8864 173.173 16.9924 169.632 16.9924C166.092 16.9924 163.222 19.8864 163.222 23.4564C163.222 27.0263 166.092 29.9204 169.632 29.9204Z"
//           fill="#081265"
//         />
//         <path
//           fill-rule="evenodd"
//           clip-rule="evenodd"
//           d="M93.8226 36.0273C97.5345 35.0245 100.417 32.8382 102.477 29.4671L97.5479 26.3359C96.4335 28.3708 94.6778 29.7148 92.2869 30.3608C90.8086 30.7602 89.3602 30.6941 87.9519 30.1599C86.5437 29.6257 85.554 28.7117 84.9824 27.4239L102.794 22.6115L101.82 18.9444C100.87 15.3711 98.9502 12.8741 96.0587 11.4638C93.1664 10.0539 90.0443 9.80183 86.6812 10.7105C83.3191 11.6189 80.7076 13.5508 78.8457 16.5065C76.9907 19.4655 76.5709 22.8761 77.6027 26.759C78.6344 30.6416 80.6651 33.4053 83.7053 35.0567C86.7406 36.7044 90.1116 37.03 93.8226 36.0273ZM83.8753 22.5072C83.2246 20.0586 85.0832 17.1619 88.1086 16.3445C91.1339 15.5271 94.1799 17.0987 94.8306 19.5473L83.8753 22.5072Z"
//           fill="#081265"
//         />
//         <path
//           fill-rule="evenodd"
//           clip-rule="evenodd"
//           d="M12.9622 33.6141L11.9494 36.6779C11.4637 38.147 12.5482 39.6642 14.084 39.6642H35.4619C37.9472 39.6642 39.962 37.6327 39.962 35.1267L39.9626 10.9263C39.9626 8.42023 37.9479 6.38863 35.4625 6.38863H30.9618L24.9617 24.5389H34.1118L31.1118 33.6141H12.9622Z"
//           fill="#1630AC"
//         />
//         <path
//           fill-rule="evenodd"
//           clip-rule="evenodd"
//           d="M12.967 33.6115H8.46688C5.98156 33.6115 3.96681 31.5799 3.96681 29.0739V4.87349C3.96681 2.36746 5.98156 0.335915 8.46688 0.335915H29.8455C31.3813 0.335915 32.4657 1.85304 31.9801 3.32215L30.9672 6.38601H12.967L9.96691 15.4612H18.9671L12.967 33.6115Z"
//           fill="#081265"
//         />
//       </svg>
//     );
//   };

  const ProgressBar = ({ currentStep }) => {
    const totalSteps = 3;
    const progressPercentage = (currentStep / totalSteps) * 100;

    return (
      <div
        style={{
          width: "100%",
          backgroundColor: "#EAEFFD",
          height: "8px",
          borderRadius: "100px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: "100%",
            transformOrigin: "left",
            transform: `scaleX(${progressPercentage / 100})`,
            backgroundColor: "#325CEB",
            height: "100%",
            borderRadius: "100px",
            transition: "transform 0.3s ease-in-out",
            position: "relative",
          }}
        />
        {/* 步骤分隔线 */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: "33.33%",
            width: "8px",
            height: "100%",
            backgroundColor: "#EAEFFD",
            zIndex: 1,
          }}
        />
        <div
          style={{
            position: "absolute",
            top: 0,
            left: "66.66%",
            width: "8px",
            height: "100%",
            backgroundColor: "#EAEFFD",
            zIndex: 1,
          }}
        />
      </div>
    );
  };

  // ---------- 2026-09 redesign: the page's pieces ----------

  const loPhone = loInfo?.phone || "";
  const telHref = loPhone ? `tel:${loPhone.replace(/[^\d+]/g, "")}` : "";
  const lenderName = !customerId || customerId === "gmcc" ? "GMCC" : brandName || t("yourLender");
  const consentText = t("consent", { lender: lenderName });

  const header = (showSecure: boolean) => (
    <header className="gop-header">
      <div className="gop-logo">{gmcclogo()}</div>
      <div className="gop-header-tools">
        <div className="gop-header-right">
          {showSecure && (
            <span className="gop-header-secure">
              <LockOutlined rev={undefined} />
              {t("secure")}
            </span>
          )}
          {showSecure && loPhone && <span className="gop-header-divider" />}
          {loPhone && (
            <span>
              {t("questions")} <a href={telHref}>{loPhone}</a>
            </span>
          )}
        </div>
        <LanguagePicker lang={lang} onChange={changeLang} />
        {loPhone && (
          <a className="gop-header-call" href={telHref} aria-label={t("callLO")}>
            <PhoneOutlined rev={undefined} />
          </a>
        )}
      </div>
    </header>
  );

  // Every click answers: copied, or -- where the page may not write the
  // clipboard (permission denied, an embedding frame without clipboard-write)
  // -- the address itself, so the button is never "nothing happened".
  const copyLOEmail = (email: string) => {
    const copied = () => message.success(t("emailCopied", { email }));
    const shown = () => message.info(t("emailShown", { email }), 6);
    const legacyCopy = () => {
      const el = document.createElement("textarea");
      el.value = email;
      el.setAttribute("readonly", "");
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.select();
      let ok = false;
      try {
        ok = document.execCommand("copy");
      } catch (e) {
        ok = false;
      }
      document.body.removeChild(el);
      return ok;
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(email).then(copied, () => (legacyCopy() ? copied() : shown()));
    } else if (legacyCopy()) {
      copied();
    } else {
      shown();
    }
  };

  const loanOfficerCard = () => {
    const name = `${loInfo?.first_name || ""} ${loInfo?.last_name || ""}`.trim();
    if (!loInfo || !name) return null;
    const initials = `${loInfo.first_name?.[0] || ""}${loInfo.last_name?.[0] || ""}`.toUpperCase() || "LO";
    const actions = [
      loInfo.phone && (
        <a key="call" className="gop-btn-ghost" href={telHref}>
          <PhoneOutlined rev={undefined} />
          {t("call")}
        </a>
      ),
      loInfo.email && (
        // mailto: alone does nothing for anyone without a desktop mail app
        // (webmail users, most of them), so the address is also copied.
        <a key="email" className="gop-btn-ghost" href={`mailto:${loInfo.email}`} onClick={() => copyLOEmail(loInfo.email)}>
          <MailOutlined rev={undefined} />
          {t("email")}
        </a>
      ),
    ].filter(Boolean);
    return (
      <div className="gop-panel">
        <div className="gop-eyebrow">{t("yourLO")}</div>
        <div className="gop-lo" style={actions.length ? undefined : { marginBottom: 0 }}>
          <div className="gop-lo-avatar">{initials}</div>
          <div>
            <div className="gop-lo-name">{name}</div>
            {loInfo.nmls && <div className="gop-lo-nmls">NMLS #{loInfo.nmls}</div>}
            {loInfo.email && <div className="gop-lo-email">{loInfo.email}</div>}
          </div>
        </div>
        {actions.length > 0 && (
          <div className={`gop-lo-actions${actions.length === 1 ? " is-single" : ""}`}>{actions}</div>
        )}
      </div>
    );
  };

  // One numbered card. On a phone only the current step's card shows; the
  // rest stay mounted (hidden) so their fields keep their values and rules.
  // A section that is not on this customer's form renders nothing.
  const sectionCard = (
    id: string,
    title: React.ReactNode,
    sub: string,
    body: React.ReactNode,
    opts: { right?: React.ReactNode; bodyClass?: string } = {}
  ) => {
    const index = sections.findIndex((s) => s.id === id);
    if (index < 0) return null;
    return (
      <section
        id={id}
        className={`gop-section${isMobile && step !== index ? " is-off" : ""}`}
        aria-labelledby={`${id}-title`}
      >
        <div className="gop-section-head">
          <span className="gop-section-num">{index + 1}</span>
          <div className="gop-section-titles">
            <div className="gop-section-title" id={`${id}-title`}>
              {title}
            </div>
            {sub && <div className="gop-section-sub">{sub}</div>}
          </div>
          {opts.right}
        </div>
        <div className={opts.bodyClass || "gop-section-body"}>{body}</div>
      </section>
    );
  };

  const toggleCoBorrower = (next: boolean) => {
    setShowCoBorrower(next);
    form.setFieldsValue({ hasCoBorrower: next });
  };

  const aboutSection = () =>
    sectionCard(
      "gop-about",
      t("secAbout"),
      t("aboutSub"),
      <>
        <div className="gop-grid">
          <Form.Item label={t("firstName")} name="firstName" rules={[{ required: true, message: t("reqFirstName") }]}>
            <Input placeholder="Jane" autoComplete="given-name" />
          </Form.Item>
          <Form.Item label={t("lastName")} name="lastName" rules={[{ required: true, message: t("reqLastName") }]}>
            <Input placeholder="Doe" autoComplete="family-name" />
          </Form.Item>
          <Form.Item
            label={t("emailLabel")}
            name="email"
            rules={[
              { required: true, message: t("reqEmail") },
              { type: "email", message: t("badEmail") },
            ]}
          >
            <Input placeholder="jane@email.com" inputMode="email" autoComplete="email" />
          </Form.Item>
          <Form.Item
            label={t("mobilePhone")}
            name="phone"
            rules={[{ required: true, message: t("reqPhone") }, { validator: validatePhoneNumber }]}
            getValueFromEvent={(e) => com.fixPhoneInput(e.target.value)}
          >
            <Input placeholder="(555) 555-5555" maxLength={14} inputMode="tel" autoComplete="tel" />
          </Form.Item>
          <Form.Item
            label={
              <>
                {t("ssn")}<span className="gop-label-note">{t("optionalParen")}</span>
              </>
            }
            name="ssn"
            rules={[{ validator: checkSSNLength }]}
            getValueFromEvent={(e) => formatSSN(e.target.value)}
            extra={t("ssnExtra")}
          >
            <SSNAntdInput
              placeholder="•••-••-••••"
              maxLength={11}
              isVisible={showSSN}
              onToggleVisibility={() => setShowSSN(!showSSN)}
            />
          </Form.Item>
          <Form.Item label={t("creditScore")} name="ficoScore" extra={t("creditScoreExtra")}>
            <Select placeholder={t("selectRange")} options={ficoRanges(t)} />
          </Form.Item>
          <div className="gop-span-2">
            <div className="gop-toggle-row" onClick={() => toggleCoBorrower(!showCoBorrower)}>
              <div>
                <div className="gop-toggle-title">{t("coToggleTitle")}</div>
                <div className="gop-toggle-sub">{t("coToggleSub")}</div>
              </div>
              <Form.Item name="hasCoBorrower" valuePropName="checked" noStyle>
                <Switch
                  aria-label={t("addCoBorrower")}
                  onClick={(_checked, e) => e.stopPropagation()}
                  onChange={(checked) => setShowCoBorrower(checked)}
                />
              </Form.Item>
            </div>
          </div>
        </div>

        {showCoBorrower && (
          <>
            <div className="gop-subhead">{t("coBorrower")}</div>
            <div className="gop-grid">
              <Form.Item
                label={t("firstName")}
                name="coFirstName"
                rules={[{ required: true, message: t("reqCoFirstName") }]}
              >
                <Input placeholder={t("firstName")} />
              </Form.Item>
              <Form.Item
                label={t("lastName")}
                name="coLastName"
                rules={[{ required: true, message: t("reqCoLastName") }]}
              >
                <Input placeholder={t("lastName")} />
              </Form.Item>
              <Form.Item
                label={t("emailLabel")}
                name="coEmail"
                rules={[
                  { required: true, message: t("reqCoEmail") },
                  { type: "email", message: t("badEmail") },
                ]}
              >
                <Input placeholder="name@email.com" inputMode="email" />
              </Form.Item>
              <Form.Item
                label={t("mobilePhone")}
                name="coPhone"
                rules={[
                  { required: true, message: t("reqCoPhone") },
                  { validator: validatePhoneNumber },
                ]}
                getValueFromEvent={(e) => com.fixPhoneInput(e.target.value)}
              >
                <Input placeholder="(555) 555-5555" maxLength={14} inputMode="tel" />
              </Form.Item>
              <Form.Item
                label={
                  <>
                    {t("ssn")}<span className="gop-label-note">{t("optionalParen")}</span>
                  </>
                }
                name="coSsn"
                rules={[{ validator: checkSSNLength }]}
                getValueFromEvent={(e) => formatSSN(e.target.value)}
              >
                <SSNAntdInput
                  placeholder="•••-••-••••"
                  maxLength={11}
                  isVisible={showCoSSN}
                  onToggleVisibility={() => setShowCoSSN(!showCoSSN)}
                />
              </Form.Item>
              <Form.Item label={t("creditScore")} name="coFicoScore">
                <Select placeholder={t("selectRange")} options={ficoRanges(t)} />
              </Form.Item>
            </div>
          </>
        )}

        <div className="gop-mnote">
          <LockOutlined rev={undefined} />
          {t("encryptedNote")}
        </div>
      </>
    );

  const loanSection = () =>
    sectionCard(
      "gop-loan",
      t("secLoan"),
      t("loanSub"),
      <>
        <Form.Item label={t("iWouldLike")} name="loanPurpose" initialValue="purchase">
          <ChoiceCards
            onPick={(v: string) => {
              setLoanPurpose(v);
              if (v !== "purchase") form.setFieldsValue({ stillShopping: false });
            }}
          />
        </Form.Item>

        {loanPurpose === "purchase" ? (
          <>
            <div className="gop-grid">
              <Form.Item label={t("purchasePrice")} name="purchasePrice">
                <InputNumber {...moneyInputProps} placeholder="750,000" />
              </Form.Item>
              <div className="gop-dp">
                <Form.Item
                  label={t("downPayment")}
                  name="downPayment"
                  dependencies={["purchasePrice"]}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const price = Number(getFieldValue("purchasePrice") || 0);
                        if (value && price && Number(value) > price) {
                          return Promise.reject(new Error(t("downPaymentTooBig")));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <InputNumber {...moneyInputProps} placeholder="150,000" />
                </Form.Item>
                <DownPaymentShare form={form} />
              </div>
            </div>
            <div className="gop-inline-question">
              <span className="gop-inline-question-label">{t("firstTimeBuyer")}</span>
              <Form.Item name="isFirstTimeBuyer">
                <Radio.Group optionType="button" className="gop-seg" options={yesNo(t)} />
              </Form.Item>
            </div>
          </>
        ) : (
          <>
            <div className="gop-grid">
              <Form.Item label={t("remainingBalance")} name="remainingBalance">
                <InputNumber {...moneyInputProps} placeholder="450,000" />
              </Form.Item>
              <Form.Item label={t("currentRate")} name="currentInterestRate">
                <InputNumber
                  min={0}
                  max={100}
                  step={0.01}
                  controls={false}
                  suffix="%"
                  placeholder="6.5"
                  // No formatter: one that rewrites the value on every
                  // keystroke (toFixed(2)) fought the cursor while typing,
                  // and rounding to two places turned an eighth like 5.875
                  // into 5.88. min/max still clamp on blur.
                  stringMode
                />
              </Form.Item>
              <Form.Item label={t("cashOut")} name="borrowAdditionalCash">
                <InputNumber {...moneyInputProps} placeholder="0" />
              </Form.Item>
            </div>
            <div className="gop-inline-question">
              <span className="gop-inline-question-label">{t("secondMortgage")}</span>
              <Form.Item name="hasSecondMortgage">
                <Radio.Group optionType="button" className="gop-seg" options={yesNo(t)} />
              </Form.Item>
            </div>
          </>
        )}
      </>
    );

  const propertySection = () =>
    sectionCard(
      "gop-property",
      t("secProperty"),
      "",
      <>
        {loanPurpose === "purchase" && (
          <Form.Item name="stillShopping" valuePropName="checked" className="gop-shopping">
            <Checkbox
              onChange={(e) => {
                if (e.target.checked) clearPropertyAddress();
              }}
            >
              {t("stillShopping")}
            </Checkbox>
          </Form.Item>
        )}
        {!(loanPurpose === "purchase" && stillShopping) && (
          <>
            <div className="gop-address-row">
              <div className="gop-address">
                <SearchOutlined rev={undefined} className="gop-address-icon" />
                <Form.Item
                  label={t("streetAddress")}
                  name="propertyAddress"
                  extra={t("streetExtra")}
                >
                  <AddressAutocomplete
                    id="PropertyAddress"
                    required={false}
                    border={true}
                    defaultValue={addressValue}
                    hideLabel={true}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      addressValueRef.current = newValue;
                      // 当用户手动输入时，清空其他相关字段
                      if (!newValue) {
                        form.setFieldsValue({
                          propertyState: undefined,
                          propertyCounty: undefined,
                          propertyCity: "",
                          propertyZip: "",
                        });
                        setSelectedState("");
                      }
                    }}
                    // A JSX string attribute keeps its backslashes literally
                    // (the browser received "\\s"); a JS string gives it "\s".
                    pattern={"^[0-9]+\\s[\\p{L}\\p{N} !:,&\\(\\)'?#+\\/@\\-]+$"}
                    placeholder={t("addressPlaceholder")}
                    goodfeedback={t("addressGood")}
                    badfeedback={t("addressBad")}
                    onCompletion={(address) => {
                      let state = com.getSubaddress(address, "administrative_area_level_1", "short_name");
                      let zip = com.getSubaddress(address, "postal_code", "long_name");
                      let county = com.getSubaddress(address, "administrative_area_level_2", "long_name");
                      let num = com.getSubaddress(address, "street_number", "long_name");
                      let short = com.getSubaddress(address, "route", "short_name");
                      let city = com.getSubaddress(address, "locality", "long_name");
                      let stradd = num + " " + short + ", " + city + ", " + state + " " + zip;

                      const fullStateName = com.capitalize(com.convertAbbStateToFull(state));
                      setSelectedState(fullStateName);

                      // 设置地址状态和表单字段值
                      setAddressValue(stradd);
                      addressValueRef.current = stradd;
                      form.setFieldsValue({
                        propertyState: fullStateName,
                        propertyCounty: county,
                        propertyCity: city,
                        propertyZip: zip,
                        propertyAddress: stradd,
                      });
                    }}
                    size="md"
                  />
                </Form.Item>
              </div>
              <Form.Item label={t("unit")} name="propertyUnit">
                <Input placeholder={t("unitPlaceholder")} />
              </Form.Item>
            </div>

            <div className="gop-grid gop-grid-4">
              <Form.Item label={t("city")} name="propertyCity">
                <Input placeholder={t("city")} autoComplete="address-level2" />
              </Form.Item>
              <Form.Item label={t("state")} name="propertyState">
                <Select
                  placeholder={t("select")}
                  showSearch
                  optionFilterProp="label"
                  options={getStateOptions().filter((o) => o.value)}
                  onChange={(value) => {
                    setSelectedState(value);
                    // 清空县的选择，因为州改变了
                    form.setFieldsValue({ propertyCounty: undefined });
                  }}
                />
              </Form.Item>
              <Form.Item label={t("county")} name="propertyCounty">
                <Select
                  placeholder={t("select")}
                  showSearch
                  optionFilterProp="label"
                  options={getCountyOptions(selectedState).filter((o) => o.value)}
                />
              </Form.Item>
              <Form.Item label={t("zip")} name="propertyZip">
                <Input placeholder="00000" inputMode="numeric" autoComplete="postal-code" />
              </Form.Item>
            </div>
          </>
        )}

        <div className="gop-grid">
          <Form.Item label={t("propertyValue")} name="propertyValue">
            <InputNumber {...moneyInputProps} placeholder="750,000" />
          </Form.Item>
          <Form.Item label={t("propertyUse")} name="propertyUse">
            <Select
              placeholder={t("select")}
              allowClear
              options={propertyUseOptions(t)}
              onChange={(value) => {
                // Declaration A asks the same thing; keep them in step.
                // Clearing has to clear the derived answer as well: a stale
                // "no" would otherwise stay behind, and the declaration radio
                // locks itself whenever Property Use is set, so the borrower
                // would be stuck on an answer they never gave.
                const a = value ? (value === "primary" ? "yes" : "no") : "";
                form.setFieldsValue({
                  declaration_A: a || undefined,
                  co_declaration_A: a || undefined,
                });
                setDeclarationA(a);
                setCoDeclarationA(a);
              }}
            />
          </Form.Item>
        </div>
      </>
    );

  // One borrower's 14 questions. prefix "" is the primary borrower
  // (declaration_A...), "co_" the co-borrower (co_declaration_A...); decA/decC/
  // decA1 and their setters drive that borrower's follow-up questions.
  const declarationRows = (prefix, decA, decC, decA1, setDecA, setDecC, setDecA1) => {
    // Question A is answered by "How will you use it?"; the server derives it
    // from there regardless, so the control only shows the answer.
    const occupancyLocked = !!form.getFieldValue("propertyUse");
    return DECLARATION_QUESTIONS[lang].map((question, index) => {
      const key = DECLARATION_KEYS[index];
      return (
        <div key={`${prefix}${key}`} className="gop-decl">
          <div className="gop-decl-row">
            <span className="gop-decl-letter">{key}</span>
            <span className="gop-decl-q">
              <span className="gop-decl-inline">{key}</span>
              <span>
                {question}
                {index === 0 && occupancyLocked && <span className="gop-decl-note">{t("answeredInProperty")}</span>}
              </span>
            </span>
            <Form.Item
              name={`${prefix}declaration_${key}`}
              rules={[{ required: true, message: t("selectAnswer") }]}
              className="gop-decl-answer"
            >
              <Radio.Group
                optionType="button"
                className="gop-seg"
                options={yesNo(t)}
                disabled={index === 0 && occupancyLocked}
                onChange={(e) => {
                  if (index === 0) setDecA(e.target.value);
                  if (index === 2) setDecC(e.target.value);
                }}
              />
            </Form.Item>
          </div>

          {/* 问题A为yes时的额外问题 */}
          {index === 0 && decA === "yes" && (
            <div className="gop-followup">
              <div className="gop-inline-question">
                <span className="gop-inline-question-label">
                  {t("ownedAnother")}
                </span>
                <Form.Item
                  name={`${prefix}declaration_A1`}
                  rules={[{ required: true, message: t("selectAnswer") }]}
                >
                  <Radio.Group
                    optionType="button"
                    className="gop-seg"
                    options={yesNo(t)}
                    onChange={(e) => setDecA1(e.target.value)}
                  />
                </Form.Item>
              </div>
              {decA1 === "yes" && (
                <div className="gop-grid">
                  <Form.Item
                    label={t("ownedType")}
                    name={`${prefix}declaration_A1_Type`}
                    rules={[{ required: true, message: t("reqOwnedType") }]}
                  >
                    <Select
                      placeholder={t("select")}
                      options={[
                        { value: "primary", label: t("usePrimary") },
                        { value: "secondary", label: t("typeSecondary") },
                        { value: "investment", label: t("typeInvestment") },
                        { value: "fha", label: t("useFha") },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item
                    label={t("titleHeld")}
                    name={`${prefix}declaration_A1_Status`}
                    rules={[{ required: true, message: t("reqTitleHeld") }]}
                  >
                    <Select
                      placeholder={t("select")}
                      options={[
                        { value: "by-yourself", label: t("titleSole") },
                        { value: "jointly-with-spouse", label: t("titleSpouse") },
                        { value: "jointly-with-other", label: t("titleOther") },
                      ]}
                    />
                  </Form.Item>
                </div>
              )}
            </div>
          )}

          {/* 问题C为yes时的额外问题 */}
          {index === 2 && decC === "yes" && (
            <div className="gop-followup">
              <Form.Item
                label={t("borrowedAmount")}
                name={`${prefix}declaration_C1`}
                rules={[{ required: true, message: t("reqAmount") }]}
              >
                <InputNumber {...moneyInputProps} placeholder={t("amountPlaceholder")} />
              </Form.Item>
            </div>
          )}
        </div>
      );
    });
  };

  const declarationsSection = () =>
    sectionCard(
      "gop-declarations",
      t("secDeclarations"),
      t("declSub"),
      <>
        {showCoBorrower && <div className="gop-decl-group-title">{t("borrower")}</div>}
        {declarationRows(
          "",
          declarationA,
          declarationC,
          declarationA1,
          setDeclarationA,
          setDeclarationC,
          setDeclarationA1
        )}
        {showCoBorrower && (
          <>
            <div className="gop-decl-group-title">{t("coBorrower")}</div>
            {declarationRows(
              "co_",
              coDeclarationA,
              coDeclarationC,
              coDeclarationA1,
              setCoDeclarationA,
              setCoDeclarationC,
              setCoDeclarationA1
            )}
          </>
        )}
        <div className="gop-decl-footnote">
          {t("declFootnote")}
        </div>
      </>,
      { right: <DeclarationCount form={form} withCo={showCoBorrower} />, bodyClass: "gop-decl-body" }
    );

  const fileIcon = (item) => {
    const name = `${item?.name || ""}`.toLowerCase();
    if (item?.type === "application/pdf" || name.endsWith(".pdf")) return <FilePdfOutlined rev={undefined} />;
    if (`${item?.type || ""}`.startsWith("image/")) return <FileImageOutlined rev={undefined} />;
    return <FileOutlined rev={undefined} />;
  };

  const dropzone = (props) => (
    <Dragger {...props} className="gop-drop">
      <div className="gop-drop-inner">
        <span className="gop-drop-icon">
          <UploadOutlined rev={undefined} />
        </span>
        <span className="gop-drop-text">
          <span className="gop-drop-title">
            <em>{t("chooseFiles")}</em>
            {t("orDragHere")}
          </span>
          <span className="gop-drop-sub">{t("dropSub")}</span>
        </span>
      </div>
    </Dragger>
  );

  // Files are sent when the application is submitted, so a row says the
  // file is ready rather than uploaded.
  const fileRows = (list, onDelete) =>
    list.length > 0 && (
      <div className="gop-files">
        {list.map((item, index) => (
          <div key={item.uid} className="gop-file">
            <span className="gop-file-icon">{fileIcon(item)}</span>
            <span className="gop-file-text">
              <span className="gop-file-name">{item.name}</span>
              <span className="gop-file-size">{formatBytes(item.size)}</span>
            </span>
            <span className="gop-file-status">
              <CheckOutlined rev={undefined} />
              {t("ready")}
            </span>
            <button
              type="button"
              className="gop-file-remove"
              aria-label={t("removeFile", { name: item.name })}
              onClick={() => onDelete(index)}
            >
              <CloseOutlined rev={undefined} />
            </button>
          </div>
        ))}
      </div>
    );

  const examples = (
    <div>
      <div className="gop-examples-title">{t("forExample")}</div>
      <div className="gop-chips">
        {EXAMPLE_DOCUMENTS.map((d) => (
          <span key={d} className="gop-chip">
            {t(d)}
          </span>
        ))}
      </div>
    </div>
  );

  const documentsSection = () =>
    sectionCard(
      "gop-documents",
      <>
        {t("secDocuments")} <span className="gop-section-title-note">{t("optionalParen")}</span>
      </>,
      t("docsSub"),
      showCoBorrower ? (
        <>
          {examples}
          <div>
            <div className="gop-upload-label">{t("yourDocuments")}</div>
            {dropzone(draggerProps)}
            {fileRows(fileList, handleDeleteDoc)}
          </div>
          <div>
            <div className="gop-upload-label">{t("coDocuments")}</div>
            {dropzone(coDraggerProps)}
            {fileRows(coFileList, handleDeleteCoDoc)}
          </div>
          <p className="gop-mconsent">{consentText}</p>
        </>
      ) : (
        <>
          {dropzone(draggerProps)}
          {examples}
          {fileRows(fileList, handleDeleteDoc)}
          <p className="gop-mconsent">{consentText}</p>
        </>
      )
    );

  const goToStep = (next: number) => {
    setStep(next);
    window.scrollTo({ top: 0 });
  };

  const focusField = (name) => {
    setTimeout(() => {
      form.scrollToField(name, { behavior: "smooth", block: "center" });
      // Focus once the smooth scroll has settled; focusing first makes the
      // browser jump there and fight it.
      setTimeout(() => form.getFieldInstance?.(name)?.focus?.(), 500);
    }, 50);
  };

  // Phone layout: check only this step's fields before moving on, so the
  // next steps do not open already covered in errors.
  const continueStep = async () => {
    const names = Object.keys(form.getFieldsValue()).filter((n) => stepOfField(n) === step);
    try {
      await form.validateFields(names);
      goToStep(step + 1);
    } catch (error: any) {
      const firstBad = error?.errorFields?.[0]?.name;
      if (firstBad) focusField(firstBad);
    }
  };

  const submitApplication = async () => {
    try {
      await form.validateFields();
    } catch (error: any) {
      // A missed required field used to leave the button doing nothing at
      // all: the error marks the field, but the form is long enough that it
      // is off-screen, so the borrower sees a dead button. Take them to the
      // first one instead (on a phone, to its step).
      const firstBad = error?.errorFields?.[0]?.name;
      if (!firstBad) {
        console.log("Form validation failed:", error);
        return;
      }
      if (isMobile) setStep(stepOfField(String(firstBad[0])));
      focusField(firstBad);
      message.error(t("completeHighlighted"));
      return;
    }
    await handleOneTimeSubmission(form.getFieldsValue());
  };

  const submitButton = (
    <button type="button" className="gop-btn-primary" disabled={loading} onClick={submitApplication}>
      {loading ? (
        t("submitting")
      ) : (
        <>
          {t("submitApplication")} <ArrowRightOutlined rev={undefined} />
        </>
      )}
    </button>
  );

  // 在组件内顶部添加 parseRate 函数
  const parseRate = (value: string) => {
    if (!value) return undefined;
    const parsed = parseFloat(value.replace(/[^\d.]/g, ''));
    if (isNaN(parsed)) return undefined;
    if (parsed < 0) return 0;
    if (parsed > 100) return 100;
    return Math.round(parsed * 100) / 100;
  };

  return (
    <LangContext.Provider value={lang}>
      <div className="gop">
      {header(currentFlow === "form")}

      {currentFlow === "form" && (
        <div className="gop-layout">
          <aside className="gop-aside">
            <ProgressPanel form={form} fileCount={fileList.length + coFileList.length} sections={sections} />
            {loanOfficerCard()}
          </aside>

          <main className="gop-main">
            <MobileProgress step={step} sections={sections} />
            <div className="gop-intro">
              <div className="gop-title" role="heading" aria-level={1}>
                {t("pageTitle")}
              </div>
            </div>

            {/* One submit: registers the borrower, creates the loan and
                enqueues the async Encompass sync. */}
            <Form
              form={form}
              layout="vertical"
              requiredMark={(label, { required }) => (
                <>
                  {label}
                  {required && <span className="gop-req">*</span>}
                </>
              )}
            >
              {aboutSection()}
              {loanSection()}
              {propertySection()}
              {declarationsSection()}
              {documentsSection()}
            </Form>

            <div className="gop-submit-card">
              <p className="gop-consent">{consentText}</p>
              {submitButton}
            </div>

            <div className="gop-mbar">
              {step > 0 && (
                <button type="button" className="gop-btn-secondary" onClick={() => goToStep(step - 1)}>
                  {t("back")}
                </button>
              )}
              {step < sections.length - 1 ? (
                <button type="button" className="gop-btn-primary" onClick={continueStep}>
                  {t("continue")}
                </button>
              ) : (
                submitButton
              )}
            </div>
          </main>
        </div>
      )}

      {currentFlow === "complete" && completePage()}
      {siteFooter}
      </div>
    </LangContext.Provider>
  );
};

export default QuickIntake;

// ==================== API 接口示例 ====================

//GMCC Quick App 接口调用流程
export const GMCC_API_Flow_Example = {
    oneTimeSubmission: {
        url: "/borrower/gmcc/ononetimesubmission",
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Cache: "no-cache",
        },
    },
    signUpSubmission: {
        step1: {
            url: "/los/generateonetimepasscode",
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Cache: "no-cache",
            },
        },
        step2: {
            url: "/los/verifycodeandsignin",
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Cache: "no-cache",
            },
        },
        // 创建borrower账户，创建loan, 传输数据，表单信息存state中，文档数据存document中， 获取mismo, 传输mismo及文档到encompass
        step3: {
            url: "/borrower/gmcc/quickapplication",
            method: "POST",
        },
        step4: {

        }
    },
    existingApplicationUpdate:{
        step1: {
            url: "/los/generateonetimepasscode",
            method: "POST",
        },
        step2: {
            url: "/los/verifycodeandsignin",
            method: "POST",
        },
        step3: {
            url: "/borrower/gmcc/quickapplication",
            method: "PATCH",
        },
        
    }
}